/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Else, If, Then } from "react-if";
import styled from "styled-components";
import {
  Box,
  Button,
  FlexContainer,
  Heading,
  Input,
  Sidebar,
  Text,
  theme,
} from "@nordcloud/gnui";
import { EstateV2Filter, useResourcesNewQuery } from "~/generated/graphql";
import { Pagination, PaginationState } from "~/components/Pagination";
import { UniversalWrap } from "~/components/Utils";
import { useQueryState } from "~/hooks";
import { filterByName, isCloudResource, isNotEmpty } from "~/tools";
import { CloudResource } from "~/views/estate/types";

const SidebarItemContainer = styled.div`
  padding: ${theme.spacing.spacing02};
  border-bottom: 1px solid ${theme.color.border.border01};
  text-align: left;

  &:hover {
    cursor: pointer;
    background-color: ${theme.color.background.ui03};
  }
`;

const SidebarFooter = styled.div`
  flex: 1;
`;

export type CloudResourceKeys = keyof CloudResource;

type Props = {
  title: string;
  onClose: () => void;
  isOpen: boolean;
  onSubmit: (resource: CloudResource) => void;
  searchFilters: EstateV2Filter;
  labelKey?: CloudResourceKeys;
};

export function CloudResourceSelector({
  title,
  onClose,
  isOpen,
  onSubmit,
  searchFilters,
  labelKey = "name",
}: Props) {
  const { state, updateQueryState } = useQueryState<PaginationState>();
  const { page, limit, search } = state;

  const [searchInput, setSearchInput] = React.useState(search ?? "");
  const { data, loading, error } = useResourcesNewQuery({
    variables: {
      limit,
      page,
      filter: searchFilters,
      query: search,
    },
  });

  const filteredItems = React.useMemo(() => {
    const resources = data?.estateV2?.resources ?? [];
    return filterByName<CloudResource>(
      resources
        .filter((resource) => isCloudResource(resource))
        .map((resource) => resource as CloudResource),
      search ?? ""
    );
  }, [data?.estateV2?.resources, search]);

  const onSearchClick = () => {
    updateQueryState({
      limit,
      page: 0,
      search: searchInput,
    });
  };

  return (
    <Sidebar
      isOpen={isOpen}
      title={title}
      footer={
        <SidebarFooter>
          <Pagination small count={data?.estateV2?.count ?? 0} />
        </SidebarFooter>
      }
      onClick={onClose}
    >
      <Box
        boxStyle="lightGrey"
        mb={theme.spacing.spacing04}
        innerSpacing="spacing02"
      >
        <FlexContainer>
          <Input
            small
            type="text"
            placeholder="Search"
            value={searchInput}
            onKeyPress={(event) => event.key === "Enter" && onSearchClick()}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSearchInput(event.target.value)
            }
          />
          <Button
            type="button"
            ml={theme.spacing.spacing02}
            icon="search"
            size="md"
            severity="low"
            onClick={onSearchClick}
          >
            Search
          </Button>
        </FlexContainer>
      </Box>
      <UniversalWrap
        loaderProps={{
          loading,
          inContent: true,
        }}
        errorProps={{
          error,
        }}
      >
        <If condition={isNotEmpty(filteredItems)}>
          <Then>
            {filteredItems.map((resource) => (
              <SidebarItemContainer
                key={resource.id}
                onClick={() => onSubmit(resource)}
              >
                <Heading level={4}>{`${resource[labelKey]}`}</Heading>
                <Text size="sm" tag="div" color={theme.color.text.text02}>
                  {resource.id}
                </Text>
              </SidebarItemContainer>
            ))}
          </Then>
          <Else>
            <Text
              css={{
                paddingTop: theme.spacing.spacing04,
                paddingBottom: theme.spacing.spacing04,
              }}
            >
              No resources matching selected criteria
            </Text>
          </Else>
        </If>
      </UniversalWrap>
    </Sidebar>
  );
}
